<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <v-toolbar flat dense class="mb-1">
        <v-spacer />
        <excel-button class="mr-2" @click="exportExcel" />
        <print-button class="mr-3" @click="print" />
        <circle-button
          icon="mdi-file-plus-outline"
          :tooltip="$t('warehouse.send_offer')"
          color="teal"
          @click="openOfferDialog"
        />
      </v-toolbar>

      <mobile-data-cards
        v-if="items.length > 0 || loading"
        :headers="mobileHeaders"
        :items="items"
        :loading="loading"
        item-key="id"
        :show-row-number="false"
      >
        <template v-slot:item.type="{ item }">
          <v-chip small :color="item.type === 'invoice' ? 'blue darken-1' : 'teal'" dark>
            {{ $t(item.type === "invoice" ? "warehouse.invoice" : "warehouse.offer") }}
          </v-chip>
        </template>
        <template v-slot:item.date="{ item }">
          {{ localeDate(item.date) }}
        </template>
        <template v-slot:item.amount="{ item }">
          {{ item.amount }} {{ item.currency }}
        </template>
        <template v-slot:item.restaurant_name="{ item }">
          {{ toLatin(item.restaurant_name) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-file-document-edit-outline"
            :tooltip="$t('overview')"
            color="primary"
            @click="openOfferDialog(item)"
          />
        </template>
      </mobile-data-cards>
      <empty-state
        v-if="!loading && items.length === 0"
        icon="mdi-file-document-outline"
        :title="$t('no_data')"
      />
    </template>

    <!-- Desktop View -->
    <template v-else>
      <empty-state
        v-if="!loading && items.length === 0"
        icon="mdi-file-document-outline"
        :title="$t('no_data')"
      />
      <v-data-table
        v-else
        :headers="headers"
        :items="items"
        :loading="loading"
        class="striped-table ordera-box-shadow"
        :items-per-page="15"
      >
        <template v-slot:top>
          <v-toolbar flat dense>
            <v-spacer />
            <excel-button class="mr-2" @click="exportExcel" />
            <print-button class="mr-3" @click="print" />
            <circle-button
              icon="mdi-file-plus-outline"
              :tooltip="$t('warehouse.send_offer')"
              color="teal"
              class="ml-2"
              @click="openOfferDialog"
            />
          </v-toolbar>
        </template>

        <template v-slot:item.type="{ item }">
          <v-chip small :color="item.type === 'invoice' ? 'blue darken-1' : 'teal'" dark>
            {{ $t(item.type === "invoice" ? "warehouse.invoice" : "warehouse.offer") }}
          </v-chip>
        </template>
        <template v-slot:item.date="{ item }">
          {{ localeDate(item.date) }}
        </template>
        <template v-slot:item.amount="{ item }">
          {{ item.amount }} {{ item.currency }}
        </template>
        <template v-slot:item.restaurant_name="{ item }">
          {{ toLatin(item.restaurant_name) }}
        </template>
        <template v-slot:item.notes="{ item }">
          <span v-if="item.notes" class="text-truncate d-block" style="max-width: 200px">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">{{ toLatin(item.notes) }}</span>
              </template>
              <span>{{ toLatin(item.notes) }}</span>
            </v-tooltip>
          </span>
          <span v-else class="grey--text">-</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-file-document-edit-outline"
            :tooltip="$t('overview')"
            color="primary"
            @click="openOfferDialog(item)"
          />
        </template>
      </v-data-table>
    </template>

    <supplier-portal-offers-invoices-dialog
      v-model="offerDialog"
      :edited-item="offerItem"
    />
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasExcel from "@/mixins/hasExcel";
import SupplierPortalOffersInvoicesDialog from "@/components/suppliers/SupplierPortalOffersInvoicesDialog.vue";
import { orderDocumentGrocery } from "@/models/warehouse";

const FAKE_ITEMS = [
  {
    id: 1,
    number: "PON-2026-001",
    type: "offer",
    date: "2026-05-10",
    amount: 1250.0,
    currency: "KM",
    restaurant_name: "Sportek",
    notes: "Ponuda za namirnice — maj 2026",
  },
  {
    id: 2,
    number: "FAK-2026-042",
    type: "invoice",
    date: "2026-05-15",
    amount: 3780.5,
    currency: "KM",
    restaurant_name: "Sportek",
    notes: "",
  },
  {
    id: 3,
    number: "PON-2026-002",
    type: "offer",
    date: "2026-05-20",
    amount: 890.0,
    currency: "KM",
    restaurant_name: "Sportek",
    notes: "Posebna ponuda za pića",
  },
  {
    id: 4,
    number: "FAK-2026-043",
    type: "invoice",
    date: "2026-06-01",
    amount: 5120.0,
    currency: "KM",
    restaurant_name: "Sportek",
    notes: "",
  },
  {
    id: 5,
    number: "PON-2026-003",
    type: "offer",
    date: "2026-06-02",
    amount: 2340.0,
    currency: "KM",
    restaurant_name: "Sportek",
    notes: "Sezonska ponuda — ljeto 2026",
  },
];

export default {
  name: "SupplierPortalOffersInvoicesTab",
  mixins: [warehouseFiltersMixin, hasPrintingV2, hasExcel],
  components: { EmptyState, MobileDataCards, SupplierPortalOffersInvoicesDialog },
  props: {
    dateRange: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      fakeItems: FAKE_ITEMS,
      offerDialog: false,
      offerItem: { ...orderDocumentGrocery, groceries: [] },
    };
  },
  computed: {
    items() {
      if (!this.dateRange || this.dateRange.length < 2 || !this.dateRange[0]) {
        return this.fakeItems;
      }
      const [from, to] = [...this.dateRange].sort();
      return this.fakeItems.filter((item) => {
        const d = (item.date || "").substring(0, 10);
        return d >= from && d <= to;
      });
    },
    headers() {
      return [
        { text: this.$t("warehouse.document_number"), value: "number" },
        { text: this.$t("type"), value: "type" },
        { text: this.$t("date"), value: "date" },
        { text: this.$t("amount"), value: "amount" },
        { text: this.$t("restaurant"), value: "restaurant_name" },
        { text: this.$t("note"), value: "notes" },
        { text: this.$t("actions"), value: "actions", sortable: false, align: "right" },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("warehouse.document_number"), value: "number" },
        { text: this.$t("type"), value: "type" },
        { text: this.$t("date"), value: "date" },
        { text: this.$t("amount"), value: "amount" },
        { text: this.$t("restaurant"), value: "restaurant_name" },
      ];
    },
    subtitle() {
      const [from, to] = [...(this.dateRange || [])].sort();
      if (!from) return "";
      const fromStr = this.localeDate(from);
      const toStr = to && to !== from ? this.localeDate(to) : null;
      return toStr ? `${fromStr} - ${toStr}` : fromStr;
    },
  },
  methods: {
    openOfferDialog(item = null) {
      this.offerItem = { ...orderDocumentGrocery, groceries: [] };
      this.offerDialog = true;
    },
    getOfferStatusColor(status) {
      const map = {
        pending: "orange",
        accepted: "green",
        rejected: "red",
        paid: "green darken-2",
        unpaid: "red darken-1",
      };
      return map[status] || "grey";
    },
    _exportHeaders() {
      return this.headers;
    },
    _exportItems() {
      return this.items.map((item) => ({
        number: item.number,
        type: this.$t(item.type === "invoice" ? "warehouse.invoice" : "warehouse.offer"),
        date: this.localeDate(item.date),
        amount: `${item.amount} ${item.currency}`,
        restaurant_name: this.toLatin(item.restaurant_name || ""),
        notes: this.toLatin(item.notes || ""),
      }));
    },
    print() {
      const { table, heading, spacer } = this.printElements;
      const title = this.$t("warehouse.offers_invoices");
      const user = this.$store.state.user.user;
      const supplier = (user.suppliers && user.suppliers[0]) || null;
      const supplierName = supplier ? this.toLatin(supplier.name) : "";
      this.printDoc(
        [
          heading(title),
          this.subtitle
            ? `<div style="text-align:center;font-size:14px;color:#555;margin-top:4px">${this.subtitle}</div>`
            : "",
          supplierName
            ? `<div style="text-align:center;font-size:13px;font-weight:600;margin-top:4px">${supplierName}</div>`
            : "",
          spacer(8),
          table(this._exportHeaders(), this._exportItems()),
        ],
        { pageTitle: title }
      );
    },
    async exportExcel() {
      const title = this.$t("warehouse.offers_invoices");
      await this.downloadExcel({
        fileName: `${title}.xlsx`,
        sheets: [
          {
            name: title.substring(0, 31),
            title,
            subtitle: this.subtitle,
            headers: this._exportHeaders(),
            items: this._exportItems(),
          },
        ],
      });
    },
  },
};
</script>
